import React, { useState } from 'react';
import {
	Typography,
	Box,
	TextField,
	MenuItem,
	Grid,
	FormControl,
	RadioGroup,
	FormControlLabel,
	Radio,
} from '@mui/material';
import {
	AllocationOption,
	InventoryStatus,
	InventoryItemAllocationQuantity,
} from 'common/modules/inventory';
import ActionDialog from 'common/components/ActionDialog';
import { useTranslation } from 'services/localization';
import { getTypedKeys } from 'common/utils/objects';
import { InventoryStatuses } from 'common/modules/inventory/constants';
import { getInventoryStatusTranslation } from '../../utils';
import {
	allocationOptionValues,
	getInventoryAllocationOptionTranslation,
} from 'views/Inventory/utils';

interface Props {
	status: InventoryStatus;
	quantity: number | null;
	allocations: InventoryItemAllocationQuantity;
	onUpdate: (newStatus: InventoryStatus, qty: number, newAllocation: AllocationOption) => void;
	onDelete: (status: InventoryStatus, qty: number) => void;
	onCancel: () => void;
}

type EditType = 'update' | 'delete';

const inventoryStatusesList = getTypedKeys(InventoryStatuses);

const getValidStatusOptions = (statusToChange: InventoryStatus) => {
	return inventoryStatusesList.filter((status) => status !== statusToChange);
};

const EditModal = (props: Props) => {
	const { t } = useTranslation();
	const statusOptions =
		props.status === 'IN_USE' ? inventoryStatusesList : getValidStatusOptions(props.status);
	const [newStatus, setNewStatus] = useState<InventoryStatus>(statusOptions[0]);
	const [newAllocation, setNewAllocation] = useState<AllocationOption>('rental');
	const [quantity, setQuantity] = useState<number>(1);
	const [editType, setEditType] = useState<EditType>('update');

	const maxQuantity =
		props.status === 'IN_USE' && newStatus !== 'IN_USE'
			? props.allocations[newAllocation] ?? 0 //Moving items from IN_USE need to be gapped to selected allocation quantity
			: props.quantity ?? 0;

	const isQuantityOverLimit = quantity > maxQuantity;
	const modalConfirmDisabled =
		!quantity || (props.quantity !== null && quantity > props.quantity) || isQuantityOverLimit;

	const handleUpdate = () => {
		if (editType === 'update') {
			props.onUpdate(newStatus, quantity, newAllocation);
		} else if (editType === 'delete') {
			props.onDelete(props.status, quantity);
		}
	};

	const handleQuantityChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
		const value = Number(e.target.value);
		if (isNaN(value)) return;
		setQuantity(value);
	};

	const handleStatusChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
		const value = e.target.value as InventoryStatus;
		setNewStatus(value);
	};

	const handleEditTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setEditType(e.target.value as EditType);
	};

	const handleAllocationChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
		const value = e.target.value as AllocationOption;
		setNewAllocation(value);
	};

	const renderAllocationSelect = () => (
		<Grid item xs={4}>
			<TextField
				select
				label={
					newStatus === 'IN_USE'
						? t('inventory.columns.allocation')
						: t('common:prepositions.from', 'From')
				}
				value={newAllocation}
				onChange={handleAllocationChange}
				fullWidth
			>
				{allocationOptionValues.map((allocation) => (
					<MenuItem key={allocation} value={allocation}>
						{getInventoryAllocationOptionTranslation(t, allocation)}
					</MenuItem>
				))}
			</TextField>
		</Grid>
	);

	return (
		<ActionDialog
			title={t('inventory.article.itemsEditTitle', 'Update status')}
			headerVariant="contained"
			btnActions={{
				primary: {
					text: t('common:actions.update'),
					disabled: modalConfirmDisabled,
					onClick: handleUpdate,
				},
				secondary: {
					text: t('common:actions.cancel'),
					onClick: props.onCancel,
				},
			}}
			onClose={props.onCancel}
		>
			<Box mt={2}>
				<Typography variant="body1">
					{t('inventory.articles.itemsEditStatus', {
						quantity: maxQuantity,
						status: getInventoryStatusTranslation(t, props.status),
						defaultValue: 'You have {{quantity}} items {{status}}',
					})}
				</Typography>
				<Box mt={2}>
					<FormControl>
						<RadioGroup value={editType} onChange={handleEditTypeChange}>
							<FormControlLabel
								value={'update'}
								control={<Radio color="primary" />}
								label={t('inventory.article.itemsEditTypeUpdate', 'Update status')}
							/>
							<FormControlLabel
								value="delete"
								control={<Radio color="primary" />}
								label={t('inventory.article.itemsEditTypeDelete', 'Remove items')}
							/>
						</RadioGroup>
					</FormControl>
				</Box>
				{editType === 'update' ? (
					<>
						<Box mt={4} mb={2}>
							<Grid container spacing={2} alignItems="center">
								<Grid item xs={4}>
									<TextField
										label={t('inventory.article.itemsEditQuantity', 'Quantity')}
										value={!quantity ? '' : quantity}
										onChange={handleQuantityChange}
										type="number"
										inputProps={{ min: 1, max: maxQuantity }}
										fullWidth
									/>
								</Grid>
								<Grid item xs={props.status === 'IN_USE' || newStatus === 'IN_USE' ? 4 : 8}>
									<TextField
										select
										label={t('inventory.article.itemsEditMarkAs', 'Mark as')}
										value={newStatus}
										onChange={handleStatusChange}
										fullWidth
									>
										{statusOptions.map((status) => (
											<MenuItem key={status} value={status}>
												{getInventoryStatusTranslation(t, status)}
											</MenuItem>
										))}
									</TextField>
								</Grid>
								{(props.status === 'IN_USE' || newStatus === 'IN_USE') && renderAllocationSelect()}
							</Grid>
						</Box>
						<Typography variant="body2">
							{isQuantityOverLimit && props.status === 'IN_USE'
								? `Only ${maxQuantity} item(s) from ${newAllocation} can be updated`
								: t('inventory.articles.itemsEditMarkStatus', {
										quantity,
										status: getInventoryStatusTranslation(t, props.status),
										newStatus: getInventoryStatusTranslation(t, newStatus),
										defaultValue:
											'This will mark {{quantity}} of your {{status}} items as {{newStatus}}.',
								  })}
						</Typography>
					</>
				) : (
					<>
						<Box mt={4} mb={2}>
							<Grid container spacing={2} alignItems="center">
								<Grid item xs={4}>
									<TextField
										label={t('inventory.article.itemsEditQuantity', 'Quantity')}
										value={!quantity ? '' : quantity}
										onChange={handleQuantityChange}
										type="number"
										inputProps={{ min: 1, max: maxQuantity }}
										fullWidth
									/>
								</Grid>
								{props.status === 'IN_USE' && renderAllocationSelect()}
							</Grid>
						</Box>
						<Typography variant="body2">
							{isQuantityOverLimit && props.status === 'IN_USE'
								? `Only ${maxQuantity} item(s) from ${newAllocation} can be removed`
								: t('inventory.articles.itemsEditRemoveStatus', {
										quantity,
										status: getInventoryStatusTranslation(t, props.status),
										defaultValue: 'This will remove {{quantity}} items from {{status}}.',
								  })}
						</Typography>
					</>
				)}
			</Box>
		</ActionDialog>
	);
};

export default EditModal;
